import React, { useState, useEffect } from 'react';
import { Card, Spin, Alert, Descriptions, Tag, Divider, Avatar, Row, Col, Button, Modal, Form, Input } from 'antd';
import { UserOutlined, MailOutlined, IdcardOutlined, BankOutlined, ShopOutlined, LockOutlined, KeyOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { authApi } from '../services/api';
import useAuthStore from '../store/authStore';

const PerfilUsuario = () => {
    const [loading, setLoading] = useState(true);
    const [userProfile, setUserProfile] = useState(null);
    const [error, setError] = useState(null);
    const [isPasswordModalVisible, setIsPasswordModalVisible] = useState(false);
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordForm] = Form.useForm();
    const { user: currentUser } = useAuthStore();

    // Cargar perfil del usuario
    useEffect(() => {
        loadUserProfile();
    }, []);

    const loadUserProfile = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await authApi.perfil();
            
            if (response.data.status) {
                setUserProfile(response.data.user);
            } else {
                setError('No se pudo cargar la información del perfil');
            }
        } catch (error) {
            console.error('Error cargando perfil:', error);
            setError('Error al cargar la información del perfil');
            toast.error('Error al cargar la información del perfil', {
                position: "top-right",
                autoClose: 4000,
            });        } finally {
            setLoading(false);
        }
    };

    // Función para cambiar contraseña
    const handlePasswordChange = async (values) => {
        try {
            setPasswordLoading(true);
            
            const response = await authApi.cambiarPasswordPerfil({
                password_actual: values.password_actual,
                password_nueva: values.password_nueva
            });

            if (response.data.status) {
                toast.success('Contraseña actualizada exitosamente', {
                    position: "top-right",
                    autoClose: 4000,
                });
                setIsPasswordModalVisible(false);
                passwordForm.resetFields();
            } else {
                toast.error(response.data.message || 'Error al cambiar la contraseña', {
                    position: "top-right",
                    autoClose: 4000,
                });
            }
        } catch (error) {
            console.error('Error cambiando contraseña:', error);
            const errorMessage = error.response?.data?.message || 'Error al cambiar la contraseña';
            toast.error(errorMessage, {
                position: "top-right",
                autoClose: 4000,
            });
        } finally {
            setPasswordLoading(false);
        }
    };

    // Función para cerrar modal de contraseña
    const handlePasswordModalCancel = () => {
        setIsPasswordModalVisible(false);
        passwordForm.resetFields();
    };

    // Función para obtener color del rol
    const getRoleColor = (roleName) => {
        switch (roleName?.toLowerCase()) {
            case 'administrador':
                return 'red';
            case 'profesional dependencia':
            case 'administrativo':
                return 'blue';
            case 'auditor':
                return 'green';
            default:
                return 'default';
        }
    };

    // Función para obtener ícono del rol
    const getRoleIcon = (roleName) => {
        switch (roleName?.toLowerCase()) {
            case 'administrador':
                return '👑';
            case 'profesional dependencia':
            case 'administrativo':
                return '⚙️';
            case 'auditor':
                return '👁️';
            case 'coordinador':
                return '📋';
            default:
                return '👤';
        }
    };

    // Función para formatear fecha
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('es-CO', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        } catch {
            return 'N/A';
        }
    };

    if (loading) {
        return (
            <div className="p-6 flex justify-center items-center h-[calc(100vh-100px)]">
                <Spin size="large" tip="Cargando perfil..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <Alert
                    message="Error al cargar perfil"
                    description={error}
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    if (!userProfile) {
        return (
            <div className="p-6">
                <Alert
                    message="Sin información"
                    description="No se encontró información del perfil"
                    type="warning"
                    showIcon
                />
            </div>
        );
    }

    return (
        <div className="p-6">
            <Card
                title={
                    <div className="flex items-center gap-3">
                        <UserOutlined className="text-2xl text-blue-600" />
                        <span className="text-2xl font-bold text-gray-800">Mi Perfil</span>
                    </div>
                }
                className="shadow-lg"
            >
                <Row gutter={[24, 24]}>
                    {/* Información Personal */}
                    <Col xs={24} lg={12}>
                        <Card
                            size="small"
                            title={
                                <div className="flex items-center gap-2">
                                    <UserOutlined className="text-blue-600" />
                                    <span>Información Personal</span>
                                </div>
                            }
                            className="h-full"
                        >
                            <div className="flex flex-col items-center mb-6">
                                <Avatar
                                    size={100}
                                    icon={<UserOutlined />}
                                    className="bg-blue-600 mb-3"
                                />
                                <h3 className="text-xl font-semibold text-gray-800 text-center">
                                    {userProfile.name}
                                </h3>
                                <Tag 
                                    color={getRoleColor(userProfile.rol?.rol)} 
                                    className="mt-2"
                                    style={{ fontSize: '14px', padding: '4px 12px' }}
                                >
                                    {getRoleIcon(userProfile.rol?.rol)} {userProfile.rol?.rol}
                                </Tag>
                            </div>

                            <Descriptions column={1} size="small">
                                <Descriptions.Item 
                                    label={
                                        <span className="flex items-center gap-2">
                                            <MailOutlined />
                                            Email
                                        </span>
                                    }
                                >
                                    <span className="text-blue-600">{userProfile.email}</span>
                                </Descriptions.Item>
                                <Descriptions.Item 
                                    label={
                                        <span className="flex items-center gap-2">
                                            <IdcardOutlined />
                                            Cédula
                                        </span>
                                    }
                                >
                                    {userProfile.cedula}
                                </Descriptions.Item>
                                <Descriptions.Item label="Estado">
                                    <Tag color={userProfile.estado === 'activo' ? 'green' : 'red'}>
                                        {userProfile.estado?.toUpperCase()}
                                    </Tag>
                                </Descriptions.Item>
                            </Descriptions>
                        </Card>
                    </Col>

                    {/* Información Institucional */}
                    <Col xs={24} lg={12}>
                        <Card
                            size="small"
                            title={
                                <div className="flex items-center gap-2">
                                    <BankOutlined className="text-green-600" />
                                    <span>Información Institucional</span>
                                </div>
                            }
                            className="h-full"
                        >
                            <Descriptions column={1} size="small">
                                <Descriptions.Item 
                                    label={
                                        <span className="flex items-center gap-2">
                                            <ShopOutlined />
                                            Campus
                                        </span>
                                    }
                                >
                                    <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                                        {userProfile.campus?.campus || 'N/A'}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label="Rol">
                                    <Tag 
                                        color={getRoleColor(userProfile.rol?.rol)} 
                                        style={{ fontSize: '14px', padding: '4px 12px' }}
                                    >
                                        {getRoleIcon(userProfile.rol?.rol)} {userProfile.rol?.rol}
                                    </Tag>
                                </Descriptions.Item>
                              
                            </Descriptions>

                            <Divider className="my-4" />

                            <div className="space-y-3">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-600 mb-1">
                                        Fecha de Registro
                                    </h4>
                                    <p className="text-sm text-gray-700">
                                        {formatDate(userProfile.created_at)}
                                    </p>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-600 mb-1">
                                        Última Actualización
                                    </h4>
                                    <p className="text-sm text-gray-700">
                                        {formatDate(userProfile.updated_at)}
                                    </p>
                                </div>
                            </div>
                        </Card>
                    </Col>                </Row>

                {/* Botón para cambiar contraseña */}
                <Divider />
                <div className="text-center">
                    <Button
                        type="primary"
                        icon={<KeyOutlined />}
                        size="large"
                        onClick={() => setIsPasswordModalVisible(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                    >
                        Cambiar Contraseña
                    </Button>
                </div>
            </Card>

            {/* Modal para cambiar contraseña */}
            <Modal
                title={
                    <div className="flex items-center gap-2">
                        <LockOutlined className="text-blue-600" />
                        <span>Cambiar Contraseña</span>
                    </div>
                }
                open={isPasswordModalVisible}
                onCancel={handlePasswordModalCancel}
                footer={null}
                width={500}
            >
                <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handlePasswordChange}
                    autoComplete="off"
                >
                    <Form.Item
                        label="Contraseña Actual"
                        name="password_actual"
                        rules={[
                            { required: true, message: 'La contraseña actual es obligatoria' }
                        ]}
                    >
                        <Input.Password
                            placeholder="Ingrese su contraseña actual"
                            prefix={<LockOutlined />}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Nueva Contraseña"
                        name="password_nueva"
                        rules={[
                            { required: true, message: 'La nueva contraseña es obligatoria' },
                            { min: 6, message: 'La contraseña debe tener al menos 6 caracteres' }
                        ]}
                    >
                        <Input.Password
                            placeholder="Ingrese la nueva contraseña"
                            prefix={<KeyOutlined />}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item
                        label="Confirmar Nueva Contraseña"
                        name="password_confirmacion"
                        dependencies={['password_nueva']}
                        rules={[
                            { required: true, message: 'Confirme la nueva contraseña' },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue('password_nueva') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Las contraseñas no coinciden'));
                                },
                            }),
                        ]}
                    >
                        <Input.Password
                            placeholder="Confirme la nueva contraseña"
                            prefix={<KeyOutlined />}
                            size="large"
                        />
                    </Form.Item>

                    <Form.Item className="mb-0 mt-6">
                        <div className="flex gap-3 justify-end">
                            <Button
                                onClick={handlePasswordModalCancel}
                                disabled={passwordLoading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={passwordLoading}
                                className="bg-blue-600 hover:bg-blue-700"
                            >
                                Cambiar Contraseña
                            </Button>
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default PerfilUsuario;
